import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonVariant } from '@/styles/themes/button';
import { theme as customTheme, BREAKPOINTS } from '@/styles/themes/default';
import { IThemeProps } from '@/styles/themes/types';

import { FilledButton } from './core/Button/variants/FilledButton';
import { CloseButton } from './core/CloseButton';
import { Overlay } from './Overlay';

const ModalContainer = styled('div')<IThemeProps>`
  width: 100%;
  height: 100vh;
  color: white;
  background: ${({ theme }) => theme.legacy.colors.modalFilterFallback};
  overflow: hidden;
  padding: 72px 40px 40px 40px;

  @supports (backdrop-filter: blur(75px)) {
    @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
      background: ${({ theme }) => theme.legacy.colors.modalTransparentBackground};
      backdrop-filter: blur(75px);
    }
  }

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    padding-top: 40px;
    position: relative;
    width: 590px;
    max-height: 400px;
    height: auto;
  }
`;

const overlayStyles = css`
  background: ${customTheme.colors.overlayTransparentBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const closeButtonStyles = css`
  z-index: 2;
  position: absolute;
  top: 45px;
  right: 28px;

  @media (min-width: ${BREAKPOINTS.Tablet.min}px) {
    top: 28px;
  }
`;

const Content = styled.div<IThemeProps>`
  h2,
  p {
    margin-bottom: 32px;
  }

  h2 {
    font-size: 32px;
    line-height: 40px;
  }

  p {
    font-size: 16px;
    line-height: 24px;
  }
`;

interface IThankYouModalProps {
  onClose: () => void;
}

export const ThankYouModal: FC<IThankYouModalProps> = ({ onClose }) => {
  const { t } = useTranslation();

  return (
    <Overlay css={overlayStyles}>
      <ModalContainer>
        <CloseButton css={closeButtonStyles} onClose={onClose} size="small" />
        <Content>
          <h2>{t('expo.sponsor.thankYou.title')}</h2>

          <p>{t('expo.sponsor.thankYou.message')}</p>

          <FilledButton variant={ButtonVariant.ACTION_FILLED} onClick={onClose}>
            {t('expo.sponsor.thankYou.buttonLabel')}
          </FilledButton>
        </Content>
      </ModalContainer>
    </Overlay>
  );
};
